/* eslint-disable @typescript-eslint/camelcase */

import { useState, useMemo, useEffect } from 'react';
import { useFilterOptions } from '../../common/hooks/useFilterOptions';
import { useShops } from '../../common/hooks/useShops';
import { compareOptionsByName } from '../../utils/filtersUtils';
import { useTranslation } from 'react-i18next';
import { Field } from '@increasecard/typed-components/dist/types/Field';
import { Filter } from '@increasecard/typed-components';
import { sub } from 'date-fns';
import { useDispatch } from 'react-redux';
import { ClearingsActions } from './actions';
import { useShallowEqualSelector } from '../../utils/reduxUtils';

export function useMonthlyClearingsScreen() {
  const { t } = useTranslation('clearings');
  const dispatch = useDispatch();
  const monthlyClearings = useShallowEqualSelector((state) => state.clearings.monthlyPayments);
  const [daterange, setDaterange] = useState<Date[]>([
    sub(new Date(), { months: 1 }),
    sub(new Date(), { months: 1 })
  ]);
  const [filters, setFilters] = useState<Record<string, Filter>>({});
  const [page, setPage] = useState(1);
  const filterOptions = useFilterOptions();
  const shops = useShops();

  useEffect(() => {
    dispatch(
      ClearingsActions.fetchMonthlyStarted({
        cardBrand: filters.card_brand?.value as string,
        processorName: filters.processor_name?.value as string[],
        monthFrom: { year: daterange[0].getFullYear(), month: daterange[0].getMonth() + 1 },
        monthTo: { year: daterange[1].getFullYear(), month: daterange[1].getMonth() + 1 },
        shopIds: filters.shop_id?.value as string[],
        page
      })
    );
  }, [filters, daterange, page, dispatch]);
  const value = useMemo(
    () => ({
      page,
      setPage,
      filters,
      setFilters: (filters: Record<string, Filter>) => {
        setFilters(filters);
        setPage(1);
      },
      daterange,
      setDaterange: (daterange: Date[]) => {
        setDaterange(daterange);
        setPage(1);
      },
      filterFields:
        filterOptions &&
        ([
          {
            displayName: t('common:filters:shops:displayName'),
            name: 'shop_id',
            type: 'selectMultiple',
            options: shops
              ? shops
                  .map((shop) => ({ name: shop.name, value: shop.id }))
                  .sort(compareOptionsByName)
              : [],
            hasSearch: true,
            hasClearButton: true
          },
          {
            displayName: t('common:filters:processor:displayName'),
            name: 'processor_name',
            type: 'selectMultiple',
            options: filterOptions.processorNames
              .map((id) => ({
                name: t(`common:processorNames:${id}`),
                value: id
              }))
              .sort(compareOptionsByName)
          },
          {
            displayName: t('common:filters:cardBrand:displayName'),
            name: 'card_brand',
            type: 'selectMultiple',
            options: filterOptions.cardBrands
              .map((id) => ({
                name: t(`common:cardBrands:${id}`),
                value: id
              }))
              .sort(compareOptionsByName)
          }
        ] as Field[]),
      monthlyClearings
    }),
    [
      page,
      setPage,
      filterOptions,
      shops,
      t,
      filters,
      setFilters,
      daterange,
      setDaterange,
      monthlyClearings
    ]
  );
  return value;
}
